import { useEffect, useState } from 'react';
import { Upload, Camera, Loader2 } from 'lucide-react';
import { api, getStoredUser, errMsg, hasEntitlement, getEntitlements } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { toast } from 'sonner';
import LockedFeature from '@/components/LockedFeature';

export default function Progress() {
  const { t, lang } = useI18n();
  const [user, setUser] = useState(getStoredUser());
  const [checkins, setCheckins] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const allowed = user?.role === 'admin' || hasEntitlement('progress_access');

  const load = async () => {
    if (!allowed) return;
    const u = getStoredUser();
    try {
      const [me, c, p] = await Promise.all([
        api.get('/auth/me'),
        api.get(`/checkins/${u.id}`),
        api.get(`/photos/${u.id}`),
      ]);
      setUser(me.data);
      setCheckins(c.data || []);
      setPhotos(p.data || []);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  if (!allowed) return <LockedFeature testid="progress-locked" reason={(getEntitlements() || []).length === 0 ? 'package' : 'entitlement'} />;

  const sw = user?.start_weight, cw = user?.current_weight, tw = user?.target_weight;
  const pct = sw && cw && tw && sw !== tw ? Math.max(0, Math.min(100, Math.round(((cw - sw) / (tw - sw)) * 100))) : 0;

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          await api.post('/photos', { image: reader.result, category: 'weekly', angle: 'front' });
          toast.success('✓');
          load();
        } catch (err) { toast.error(errMsg(err, t('progress.uploadFailed'))); }
        finally { setUploading(false); }
      };
      reader.readAsDataURL(file);
    } catch { setUploading(false); toast.error(t('progress.uploadFailed')); }
  };

  return (
    <div className="px-6 pt-10 animate-fade-in" data-testid="client-progress">
      <p className="as-label">{t('progress.label')}</p>
      <h1 className="mb-6 mt-1 font-display text-3xl font-extrabold tracking-tight text-white">{t('progress.title')}</h1>

      <p className="as-section mb-3">{t('progress.weight')}</p>
      <div className="grid grid-cols-3 gap-2.5">
        {[['progress.start', sw], ['progress.current', cw], ['progress.target', tw]].map(([k, v], i) => (
          <div key={i} className="as-card p-4 text-center">
            <p className="text-[9px] font-bold tracking-[1.5px] text-asgym-muted">{t(k)}</p>
            <p className="mt-1.5 font-display text-2xl font-extrabold text-white">{v ?? '—'}<span className="text-xs text-asgym-sub">kg</span></p>
          </div>
        ))}
      </div>
      <div className="mt-4 as-card p-4">
        <div className="h-2 overflow-hidden rounded-full bg-white/[0.06]">
          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: 'linear-gradient(90deg,#00C896,#00B383)' }} />
        </div>
        <p className="mt-2 text-center text-xs font-bold text-asgym-sub">{pct}{t('progress.percent')}</p>
      </div>

      <p className="as-section mb-3 mt-8">{t('progress.checkins')}</p>
      <div className="as-card p-5 text-center">
        <p className="font-display text-4xl font-extrabold text-asgym-accent">{checkins.length}</p>
        <p className="mt-1 text-xs text-asgym-sub">{t('progress.checkinsLine')}</p>
      </div>

      <p className="as-section mb-3 mt-8">{t('progress.timeline')}</p>
      <label className="mb-4 flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-white/15 bg-white/[0.02] py-4 text-xs font-bold tracking-wide text-asgym-sub hover:border-asgym-accent/50 hover:text-asgym-accent" data-testid="upload-photo-btn">
        {uploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
        {t('progress.uploadPhoto')}
        <input type="file" accept="image/*" className="hidden" onChange={onUpload} disabled={uploading} />
      </label>
      {photos.length === 0 ? (
        <div className="as-card flex flex-col items-center gap-2 py-10 text-asgym-muted">
          <Camera size={22} /><p className="text-xs">—</p>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2 pb-4">
          {photos.map((ph) => (
            <div key={ph.id} className="overflow-hidden rounded-xl border border-white/[0.06] bg-asgym-surface">
              <img src={ph.image} alt="progress" className="aspect-[3/4] w-full object-cover" />
              <p className="p-1.5 text-center text-[9px] font-semibold text-asgym-muted">{new Date(ph.created_at).toLocaleDateString(lang === 'de' ? 'de-DE' : 'en-US', { day: '2-digit', month: 'short' })}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
